import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Newspaper, ExternalLink } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const NewsCarousel = ({ articles = [] }) => {
  const { userTheme } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (articles.length <= 1 || isPaused) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % articles.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [articles.length, isPaused]);

  if (!articles || articles.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Newspaper className="w-5 h-5 text-gray-400" />
          <h3 className="text-base font-semibold text-gray-800">Latest News</h3>
        </div>
        <p className="text-sm text-gray-500">No articles available.</p>
      </div>
    );
  }

  const article = articles[currentIndex];

  const goTo = (dir) => {
    setCurrentIndex(prev =>
      dir === 'next'
        ? (prev + 1) % articles.length
        : (prev - 1 + articles.length) % articles.length
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div
      className="rounded-xl border border-gray-200 bg-white overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100" style={{ backgroundColor: userTheme?.dashboardWidgetHeader?.backgroundColor }}>
        <div className="flex items-center gap-2">
          <Newspaper className="w-4 h-4 text-blue-600" />
          <h3 className="text-sm font-semibold text-gray-800">Latest News</h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => goTo('prev')}
            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-400 min-w-[3rem] text-center">
            {currentIndex + 1} / {articles.length}
          </span>
          <button
            onClick={() => goTo('next')}
            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Article card */}
      <div className="p-5">
        <div className="flex gap-3 sm:gap-4">
          {/* Thumbnail */}
          {article.thumbnail_url ? (
            <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
              <img
                src={article.thumbnail_url}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          ) : (
            <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-lg flex-shrink-0 bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center">
              <Newspaper className="w-6 h-6 sm:w-8 sm:h-8 text-blue-300" />
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">
              {article.title}
            </h4>
            {article.excerpt && (
              <p className="text-xs text-gray-500 line-clamp-2 mb-2">
                {article.excerpt}
              </p>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {formatDate(article.published_at)}
              </span>
              {article.url && (
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Read more <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dots */}
      {articles.length > 1 && (
        <div className="flex justify-center gap-1.5 pb-3">
          {articles.map((_, idx) => (
            <button
              key={idx}
              className={`w-1.5 h-1.5 rounded-full transition-all ${
                idx === currentIndex ? 'bg-blue-600 w-4' : 'bg-gray-300'
              }`}
              onClick={() => setCurrentIndex(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsCarousel;
